import React, { useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { Flexbox, Column } from "../theme/grid";
import styled from "styled-components";
import solasColors from "@boi/design-tokens/dist/web/boi/light/tokens.json";
import { AppContext } from "../app/app";
import { space, layout, color, flexbox, position, border } from "styled-system";
import defaultTheme from "../theme/theme-test-override";
import { boiLogo, boiUKLogo, boiLogoXs } from "../theme/boi-logos";
import { HyperLink, BackButton, NeedHelpButton } from "../simple-buttons/simple-buttons";

import { remCalc } from "../theme/style-utils-coras-nua";
import { helpMessageExists } from "../help-layer/help-layer";

const isUKPath = path => {
    return path.split("/").includes("uk");
};

const isBankAtWorkPath = path => {
    return path.split("/").includes("bank-at-work");
};

const getSearchParam = searchParams => {
    return new URLSearchParams(searchParams).get("gf_company_name") || "";
};

const getMainWebsiteUrl = isUKForm => {
    return isUKForm
        ? "https://www.bankofirelanduk.com/"
        : "https://www.bankofireland.com";
};

const getBankAtWorkSiteUrl = gfCompanyName => {
    return `https://bankatwork.bankofireland.com/${gfCompanyName}`;
};

const Header = ({ helpScreen, className = "" }) => {
    const isUKForm = isUKPath(window.location.pathname);
    const isBankAtWork = isBankAtWorkPath(window.location.pathname);
    const mainWebsiteUrl = getMainWebsiteUrl(isUKForm);
    const bankAtWorkSiteUrl = getBankAtWorkSiteUrl(
        getSearchParam(window.location.search)
    );
    const [isUXP, setIsUXP] = useState(false);
    const [isHelpActive, setIsHelpActive] = useState(false);
    const [isHelpEmpty, setIsHelpEmpty] = useState(true);
    const app = useContext(AppContext);
    const staticHelpMessage = helpScreen?.props?.children ? true : false;

    useEffect(() => {
        helpScreen?.props?.helpData &&
        helpMessageExists(helpScreen.props.helpData) &&
        setIsHelpEmpty(false)
    }, [helpScreen]);

    useEffect(() => {
        const url = new URL(window.location);
        if (
            url.searchParams.get("srcid") === "365mobile" ||
            url.pathname.includes("/external/")
        )
            setIsUXP(true);
    }, []);

    const helpModalClickEvent = (event) => {
        event.preventDefault();
        setIsHelpActive(!isHelpActive)
    }

    return (
        <>
            <Flexbox
                width="100%"
                maxWidth="unset"
                flexShrink="0"
                borderTop="0.5rem solid"
                borderColor="primaryTone"
                backgroundColor={solasColors.themeColor.base100}
                mb={[0, 0, 0, 0, 0, 24]}
            >
                <Column xs={12} mx="auto">
                    <StyledHeader className={`${className ? className : ""}`}>
                        <StyledSkipLink href="#main-content">Skip to main content</StyledSkipLink>
                        {!isUXP && (
                            <div className="header-left">
                                <BackButton
                                    className="hide-sm-down u-hide-for-print"
                                    as="a"
                                    background="transparent"
                                    bordercolor="transparent"
                                    title="Click here to go back to main website"
                                    mx="0"
                                    height="3.1875rem"
                                    href={
                                        isBankAtWork
                                            ? bankAtWorkSiteUrl
                                            : mainWebsiteUrl
                                    }
                                    data-testid="header-back-btn"
                                >
                                    Back to main website
                                </BackButton>
                            </div>
                        )}
                        {!isUXP && (
                            <div className="header-centre">
                                <a
                                    href={mainWebsiteUrl}
                                    title="Return to Bank of Ireland"
                                    className="logo"
                                >
                                    <span className="hide-sm-down u-hide-for-print">
                                        {isUKForm ? boiUKLogo : boiLogo}
                                    </span>
                                    <span className="hide-md-up">
                                        {boiLogoXs}
                                    </span>
                                </a>
                            </div>
                        )}
                        <div className="header-right">
                            {helpScreen &&
                            (!isHelpEmpty || staticHelpMessage) ? (
                                <NeedHelpButton
                                    className="u-hide-for-print help-trigger"
                                    title="Click here for help"
                                    data-testid="header-need-help-btn"
                                    onClick={helpModalClickEvent}
                                >
                                    Need help?
                                </NeedHelpButton>
                            ) : null}
                            {app.isTMOAuth ? (
                                <HyperLink
                                    title="Click here to logout"
                                    className="logout-trigger"
                                    onClick={() => app.setIsTMOAuth(false)}
                                >
                                    Logout
                                </HyperLink>
                            ) : (
                                <></>
                            )}
                        </div>
                    </StyledHeader>
                </Column>
            </Flexbox>
            <Modal
                isOpen={isHelpActive}
                className="helpLayer"
                overlayClassName="helpLayer"
                closeTimeoutMS={300}
                onRequestClose={() => setIsHelpActive(!isHelpActive)}
                shouldCloseOnOverlayClick={true}
            >
                {helpScreen}
                <BackButton
                    role="button"
                    href="#"
                    title="Close this help box"
                    order={2}
                    fontWeight="bold"
                    data-id="close"
                    onClick={helpModalClickEvent}
                >
                    Go Back
                </BackButton>
            </Modal>
        </>
    );
};

const StyledHeader = styled("header")`
    ${space}
    ${layout}
    ${color}
    ${position}
    ${flexbox}
    ${border}
    position: relative;
    .logo{
        ${
            "" /* position: absolute;
        left: calc(50% - ${remCalc(95)}/2); */
        }
        margin-top: -${remCalc(8)};
    }
    > div{
        display: flex;
        flex: 1;
        &.header-left{
            justify-content: flex-start;
        }
        &.header-centre{
            justify-content: center;
        }
        &.header-right{
            justify-content: flex-end;
        }
    }
`;
StyledHeader.defaultProps = {
    theme: {
        ...defaultTheme
    },
    pl: [4, 4, 6],
    pr: [4, 4, 6],
    color: "monotoneDark",
    display: "flex",
    height: remCalc(51),
    justifyContent: "space-between",
    left: 0,
    right: 0,
    top: 0,
    zIndex: 4 // actually equates to 3 based on theme object
};

const StyledSkipLink = styled("a")`
    position: absolute;
    top: -9999px;
    left: -9999px;
    opacity: 0;
    padding: 1em;
    text-decoration: none;
    background: rgb(0, 0, 255);
    color: rgb(255, 255, 255);
    &:focus {
        position: fixed;
        top: 80px;
        left: 20px;
        opacity: 1;
        z-index: 9999;
        color: rgb(255, 255, 255);
    }
`;

export default Header;
